import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MAGIC_PATH_SECTIONS } from "@/constants/genieAnimation";
import { genieTransitionBus } from "@/lib/genieTransition";
import { createPoseController } from "@/lib/geniePose";
import { HERO_SECTION_ID, setGenieTravellerLive, shouldApplyHeroInit, whenBasePoseReady } from "@/lib/genieHeroInit";
import { targetFor, waypointFor } from "@/lib/genieWaypoints";

gsap.registerPlugin(ScrollTrigger);

export const useGenieMagicPathAnimation = (elRef, posesRef, brokenRef, enabled) => {
    useEffect(() => {
        if (!enabled) return undefined;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

        const el = elRef.current;
        if (!el) return undefined;

        // Hero uses the in-layout static pose. Overlay stays off until Services.
        setGenieTravellerLive(false);
        gsap.set(el, { opacity: 0 });

        const poseCtrl = createPoseController(posesRef, brokenRef, { duration: 0.35 });

        let triggers = [];
        let hideTween = null;
        let revealTween = null;
        let hideResolve = null;
        let revealResolve = null;
        let activeRevealSectionId = null;

        const resolveReveal = () => {
            if (revealResolve) {
                revealResolve();
                revealResolve = null;
            }
        };

        const isHeroSection = (sectionId) => sectionId === HERO_SECTION_ID;

        const placeAt = (sectionId, { opacity = 1, instant = false } = {}) => {
            const wp = waypointFor(sectionId);
            if (!wp) return;
            poseCtrl.setPose(wp.pose, instant);
            const t = targetFor(el, wp);
            if (instant) {
                gsap.set(el, { x: t.x, y: t.y, scale: t.scale, rotation: t.rotation, opacity });
            } else {
                gsap.set(el, { x: t.x, y: t.y, scale: t.scale, rotation: t.rotation });
                gsap.to(el, { opacity, duration: 0.4, ease: "power2.out", overwrite: "auto" });
            }
        };

        const restoreStaticHero = () => {
            setGenieTravellerLive(false);
            gsap.set(el, { opacity: 0 });
            const wp = waypointFor(HERO_SECTION_ID);
            if (wp) poseCtrl.setPose(wp.pose, true);
        };

        const kill = () => {
            hideTween?.kill();
            revealTween?.kill();
            hideTween = null;
            revealTween = null;
            activeRevealSectionId = null;
            if (hideResolve) {
                hideResolve();
                hideResolve = null;
            }
            resolveReveal();
            poseCtrl.killAll();
            gsap.killTweensOf(el);
        };

        const enforcePose = (sectionId) => {
            const wp = waypointFor(sectionId);
            if (!wp) return;
            poseCtrl.setPose(wp.pose, true);
        };

        genieTransitionBus.registerGenie({
            kill,
            enforcePose,
            hide: () =>
                new Promise((resolve) => {
                    if (isHeroSection(genieTransitionBus.currentSectionId) || gsap.getProperty(el, "opacity") < 0.05) {
                        gsap.set(el, { opacity: 0 });
                        resolve();
                        return;
                    }
                    hideResolve = resolve;
                    hideTween?.kill();
                    hideTween = gsap.to(el, {
                        opacity: 0,
                        duration: 0.18,
                        ease: "power1.in",
                        overwrite: "auto",
                        onComplete: () => {
                            hideResolve = null;
                            resolve();
                        },
                    });
                }),
            reveal: (sectionId) =>
                new Promise((resolve) => {
                    revealResolve = resolve;
                    activeRevealSectionId = sectionId;
                    const wp = waypointFor(sectionId);
                    if (!wp || isHeroSection(sectionId)) {
                        restoreStaticHero();
                        activeRevealSectionId = null;
                        resolveReveal();
                        return;
                    }
                    setGenieTravellerLive(true);
                    poseCtrl.setPose(wp.pose);
                    const t = targetFor(el, wp);
                    gsap.set(el, { x: t.x, y: t.y, rotation: t.rotation });
                    revealTween?.kill();
                    revealTween = gsap.fromTo(
                        el,
                        { opacity: 0, scale: t.scale * 0.9, y: t.y + 14 },
                        {
                            opacity: 1,
                            scale: t.scale,
                            y: t.y,
                            duration: 0.42,
                            ease: "back.out(1.35)",
                            overwrite: "auto",
                            onComplete: () => {
                                if (activeRevealSectionId === sectionId) {
                                    poseCtrl.enforceActive();
                                }
                                activeRevealSectionId = null;
                                resolveReveal();
                            },
                        },
                    );
                }),
            restoreHero: () =>
                new Promise((resolve) => {
                    restoreStaticHero();
                    resolve();
                }),
            settle: (sectionId, instant = false) => {
                if (isHeroSection(sectionId)) {
                    restoreStaticHero();
                    return;
                }
                setGenieTravellerLive(true);
                if (instant) {
                    placeAt(sectionId, { instant: true, opacity: 1 });
                    return;
                }
                revealTween?.kill();
                activeRevealSectionId = sectionId;
                const wp = waypointFor(sectionId);
                if (!wp) return;
                poseCtrl.setPose(wp.pose);
                const t = targetFor(el, wp);
                gsap.set(el, { x: t.x, y: t.y, rotation: t.rotation });
                revealTween = gsap.fromTo(
                    el,
                    { opacity: 0, scale: t.scale * 0.92, y: t.y + 10 },
                    {
                        opacity: 1,
                        scale: t.scale,
                        y: t.y,
                        duration: 0.5,
                        ease: "power2.out",
                        overwrite: "auto",
                        onComplete: () => {
                            if (activeRevealSectionId === sectionId) {
                                poseCtrl.enforceActive();
                            }
                            activeRevealSectionId = null;
                        },
                    },
                );
            },
        });

        const initHeroStatic = () => {
            whenBasePoseReady(posesRef, () => {
                if (!shouldApplyHeroInit(genieTransitionBus.currentSectionId, genieTransitionBus.isTransitioning)) {
                    return;
                }
                genieTransitionBus.settle(HERO_SECTION_ID, true);
            });
        };

        const setup = () => {
            triggers.forEach((t) => t.kill());
            triggers = [];

            MAGIC_PATH_SECTIONS.forEach((sectionId) => {
                const node = document.querySelector(sectionId);
                if (!node) return;
                const wp = waypointFor(sectionId);

                if (isHeroSection(sectionId)) {
                    triggers.push(
                        ScrollTrigger.create({
                            trigger: node,
                            start: "top top",
                            end: "bottom 58%",
                            onEnterBack: () => genieTransitionBus.request(sectionId),
                        }),
                    );
                    return;
                }

                if (wp?.stay) {
                    triggers.push(
                        ScrollTrigger.create({
                            trigger: node,
                            start: "top 62%",
                            end: "bottom top",
                            onEnter: () => genieTransitionBus.request(sectionId),
                            onEnterBack: () => genieTransitionBus.request(sectionId),
                        }),
                    );
                    return;
                }

                triggers.push(
                    ScrollTrigger.create({
                        trigger: node,
                        start: "top 62%",
                        end: "bottom 38%",
                        onEnter: () => genieTransitionBus.request(sectionId),
                        onEnterBack: () => genieTransitionBus.request(sectionId),
                    }),
                );
            });
        };

        initHeroStatic();
        setup();

        let resizeTimer = null;
        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                genieTransitionBus.settle(genieTransitionBus.currentSectionId, true);
                ScrollTrigger.refresh();
            }, 250);
        };
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
            clearTimeout(resizeTimer);
            triggers.forEach((t) => t.kill());
            kill();
            genieTransitionBus.registerGenie(null);
            setGenieTravellerLive(false);
        };
    }, [enabled, elRef, posesRef, brokenRef]);
};
